import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { AlertTriangle, HelpCircle, Trash2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Asking the user before doing something.
 *
 * This replaces `window.confirm`, which the browser renders as its own box — titled
 * "localhost says" in Chrome, unstyled, untranslatable, and with the app's own
 * question squeezed into one run of text because it is the only field there is. It
 * also blocks the whole page, which hides anything the question refers to.
 *
 * The replacement keeps the shape that made `confirm` convenient — one `await` at the
 * call site, `true` or `false` back — so a guard stays a single line:
 *
 *     if (!(await confirm({ title: "Delete this?", tone: "danger" }))) return;
 *
 * A toast is deliberately not the answer here. Toasts report what has happened and
 * are dismissible and easy to miss; a question whose answer deletes something has to
 * be answered before anything happens. Use `notify` (`@/lib/notify`) for the outcome
 * afterwards, and this for the permission beforehand.
 */

export type ConfirmTone = "danger" | "warning" | "info";

export interface ConfirmOptions {
  title: string;
  /** The consequence, in its own sentence. What `window.confirm` had nowhere to put. */
  description?: string;
  /**
   * What the question is about, laid out — the rows a change touches, say — for a
   * question whose subject does not fit in a sentence. Shown under the description.
   */
  body?: React.ReactNode;
  /** The affirmative button. Name the act — "Delete", "Mark inactive" — not "OK". */
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * How much weight the question carries.
   *
   * `danger` for anything that destroys data, `warning` for a reversible change with a
   * consequence worth reading, `info` for a plain are-you-sure.
   */
  tone?: ConfirmTone;
}

type Ask = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<Ask | null>(null);

const TONE: Record<
  ConfirmTone,
  { icon: typeof AlertTriangle; iconClass: string; action: string; label: string }
> = {
  danger: {
    icon: Trash2,
    iconClass: "text-destructive",
    action: cn(buttonVariants({ variant: "destructive" })),
    label: "Delete",
  },
  warning: {
    icon: AlertTriangle,
    iconClass: "text-warning",
    action: cn(buttonVariants()),
    label: "Continue",
  },
  info: {
    icon: HelpCircle,
    iconClass: "text-muted-foreground",
    action: cn(buttonVariants()),
    label: "Continue",
  },
};

/**
 * Holds the one dialog the whole app asks through.
 *
 * One instance rather than a dialog per call site: a question is transient state, not
 * part of any page's layout, and fifteen pages each wiring up their own `open` flag
 * and pending-action ref is fifteen chances for the flag and the action to come apart.
 */
export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  // The resolver for the promise the caller is awaiting. Held in a ref because
  // settling it must not depend on a re-render having happened first.
  const resolve = useRef<((answer: boolean) => void) | null>(null);

  const ask = useCallback<Ask>((options) => {
    // A second question while one is open would strand the first caller's promise
    // forever, so the one already waiting is answered "no" before it is replaced.
    resolve.current?.(false);
    setPending(options);
    return new Promise<boolean>((done) => {
      resolve.current = done;
    });
  }, []);

  const settle = (answer: boolean) => {
    resolve.current?.(answer);
    resolve.current = null;
    setPending(null);
  };

  const tone = TONE[pending?.tone ?? "info"];
  const Icon = tone.icon;

  return (
    <ConfirmContext.Provider value={ask}>
      {children}
      <AlertDialog
        open={pending !== null}
        // Dismissing by backdrop or Escape is a "no", and has to settle the promise
        // rather than only close the dialog — the caller is still awaiting it.
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
      >
        <AlertDialogContent>
          {/*
           * The header's rule separates the question from its consequence, so it is
           * drawn only when there is a consequence to separate — otherwise it sits
           * above nothing but the body's gap and the buttons.
           *
           * The footer never draws its own. Its rule earns its place on a dialog long
           * enough to scroll under it; a question is never that long, and with both
           * rules on they closed around a band of empty background.
           */}
          <AlertDialogHeader
            className={cn(!pending?.description && !pending?.body && "border-b-0 pb-0")}
          >
            <div className="flex items-start gap-3">
              <Icon className={cn("mt-0.5 h-5 w-5 shrink-0", tone.iconClass)} />
              <AlertDialogTitle>{pending?.title}</AlertDialogTitle>
            </div>
          </AlertDialogHeader>
          {/* Indented to the title's column, past the icon, rather than to the dialog's edge. */}
          {pending?.description && (
            <AlertDialogDescription className="pl-8">{pending.description}</AlertDialogDescription>
          )}
          {pending?.body && <div className="pl-8">{pending.body}</div>}
          <AlertDialogFooter className="border-t-0 pt-0">
            <AlertDialogCancel onClick={() => settle(false)}>
              {pending?.cancelLabel ?? "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction className={tone.action} onClick={() => settle(true)}>
              {pending?.confirmLabel ?? tone.label}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

/**
 * Asks the question and resolves to the answer.
 *
 * Throws if no `ConfirmProvider` is mounted above, rather than returning `false` and
 * quietly turning every guarded action into a no-op that nobody can explain.
 */
export function useConfirm(): Ask {
  const ask = useContext(ConfirmContext);
  if (!ask) {
    throw new Error("useConfirm must be used inside a <ConfirmProvider>");
  }
  return ask;
}

/** Pre-shaped questions, so the common ones read the same everywhere they are asked. */
export function useConfirmDelete() {
  const confirm = useConfirm();
  return useMemo(
    () =>
      /**
       * @param what The thing being deleted, named — `Delete "Acme Ltd"?` reads better
       *   than `Delete this item?` and makes a mis-click obvious before it happens.
       */
      (what: string, description?: string) =>
        confirm({
          title: `Delete ${what}?`,
          description,
          confirmLabel: "Delete",
          tone: "danger",
        }),
    [confirm],
  );
}
